import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Container, Row, Col } from 'react-bootstrap';
import { Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card, CardBody, CardTitle, CardTitleWrap } from '@/shared/components/Card';
import { LoadingSpinner } from '@/shared/components/Loading';
import { useTelegramBotStats } from '@/services/telegram-bot-stats';
import TelegramBotStats, { TelegramStatsPeriod, TelegramStatsSegment } from '@/models/telegram-bot-stats';
import {
    colorAccent,
    colorBackground,
    colorBorder,
    colorHover,
    colorText,
    colorTextAdditional,
    colorBlue,
    colorGreen,
    colorRed,
    colorYellow,
    colorViolet,
    colorAdditional,
} from '@/utils/palette';

// ------------------------------------------------------------------
// Styled primitives — all text is color-bound to the theme palette so
// the page renders correctly on both light and dark mode.
// ------------------------------------------------------------------

const PageTitle = styled.h3`
    color: ${colorText};
`;

const PageSubhead = styled.p`
    color: ${colorTextAdditional};
    margin-bottom: 0;
`;

const FilterBar = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
    margin: 18px 0 20px;
    color: ${colorText};
`;

const PeriodLabel = styled.label`
    color: ${colorText};
    font-weight: 500;
    margin: 0;
`;

const PeriodSelect = styled.select`
    max-width: 220px;
    padding: 6px 10px;
    border-radius: 6px;
    background: ${colorBackground};
    color: ${colorText};
    border: 1px solid ${colorBorder};

    &:focus {
        outline: none;
        border-color: ${colorAccent};
    }
`;

const GeneratedAt = styled.small`
    color: ${colorTextAdditional};
`;

const KpiLabel = styled.div`
    color: ${colorTextAdditional};
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.6px;
`;

const KpiValue = styled.div`
    color: ${colorText};
    font-size: 30px;
    font-weight: 700;
    line-height: 1.15;
    margin-top: 6px;
`;

const KpiSub = styled.div`
    color: ${colorTextAdditional};
    font-size: 12px;
    margin-top: 6px;
`;

const EmptyState = styled.div`
    color: ${colorTextAdditional};
    padding: 24px 8px;
    text-align: center;
`;

const StatsTable = styled.table`
    width: 100%;
    color: ${colorText};
    border-collapse: collapse;

    th,
    td {
        padding: 10px 12px;
        border-bottom: 1px solid ${colorBorder};
    }

    thead th {
        color: ${colorTextAdditional};
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }

    tbody tr:hover {
        background: ${colorHover};
    }

    .num {
        text-align: right;
        font-variant-numeric: tabular-nums;
    }
`;

const TableWrap = styled.div`
    overflow-x: auto;
`;

// ------------------------------------------------------------------
// Static config
// ------------------------------------------------------------------

const PERIOD_OPTIONS: { value: TelegramStatsPeriod; label: string }[] = [
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 90 days' },
    { value: 'all', label: 'All time' },
];

// Slice colors for the persona pie chart — taken from the theme's accent
// palette so they read cleanly on both light and dark backgrounds.
const PERSONA_COLORS = {
    traderCompleted: colorAccent,
    traderInProgress: colorBlue,
    traderAbandoned: colorViolet,
    generalCompleted: colorGreen,
    generalInProgress: colorYellow,
    generalAbandoned: colorRed,
    preGate: colorAdditional,
};

// ------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------

const formatPercent = (v: number) => `${v.toFixed(1)}%`;

const formatCampaign = (c: {
    utmSource: string | null;
    utmCampaign: string | null;
    utmContent: string | null;
    utmMedium: string | null;
}) => {
    if (!c.utmSource && !c.utmCampaign && !c.utmContent && !c.utmMedium) return 'Direct / organic';
    return [c.utmSource, c.utmCampaign, c.utmContent, c.utmMedium].filter(Boolean).join(' / ');
};

// ------------------------------------------------------------------
// KPI tile — wraps styled-primitives inside a shared themed Card
// ------------------------------------------------------------------

const KpiTile = ({ label, value, sub }: { label: string; value: string | number; sub?: string }) => (
    <Card>
        <CardBody>
            <KpiLabel>{label}</KpiLabel>
            <KpiValue>{value}</KpiValue>
            {sub ? <KpiSub>{sub}</KpiSub> : null}
        </CardBody>
    </Card>
);

// Segment card — a vertical stack of the five KPIs (sessions + completion /
// signup / paid rates + a sessions-by-status breakdown) for one slice of the
// user population. Rendered three across for Total / Campaign / Organic.
const SegmentHeader = styled.div`
    color: ${colorText};
    font-weight: 700;
    font-size: 15px;
    letter-spacing: 0.2px;
    margin-bottom: 4px;
`;

const SegmentHint = styled.div`
    color: ${colorTextAdditional};
    font-size: 11.5px;
    margin-bottom: 14px;
`;

const SegmentRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px 14px;
    margin-top: 12px;
`;

const MiniLabel = styled.div`
    color: ${colorTextAdditional};
    font-size: 10.5px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const MiniValue = styled.div`
    color: ${colorText};
    font-size: 15px;
    font-weight: 600;
    margin-top: 2px;
`;

const PrimarySegmentHeader = styled(SegmentHeader)`
    color: ${colorAccent};
`;

const SegmentCard = ({ segment, hint, primary }: { segment: TelegramStatsSegment; hint: string; primary?: boolean }) => (
    <Card>
        <CardBody>
            {primary ? <PrimarySegmentHeader>{segment.label}</PrimarySegmentHeader> : <SegmentHeader>{segment.label}</SegmentHeader>}
            <SegmentHint>{hint}</SegmentHint>

            <KpiLabel>Sessions</KpiLabel>
            <KpiValue>{segment.sessions}</KpiValue>

            <SegmentRow>
                <div>
                    <MiniLabel>Completion rate</MiniLabel>
                    <MiniValue>{formatPercent(segment.completionRate)}</MiniValue>
                    <KpiSub>{segment.completedSessions} completed</KpiSub>
                </div>
                <div>
                    <MiniLabel>Signup rate</MiniLabel>
                    <MiniValue>{formatPercent(segment.signupRate)}</MiniValue>
                    <KpiSub>{segment.signedUpFromBot} signed in</KpiSub>
                </div>
                <div>
                    <MiniLabel>Paid conversion</MiniLabel>
                    <MiniValue>{formatPercent(segment.paidRate)}</MiniValue>
                    <KpiSub>{segment.paidFromBot} premium</KpiSub>
                </div>
                <div>
                    <MiniLabel>Status mix</MiniLabel>
                    <MiniValue>
                        {segment.completedSessions}/{segment.inProgressSessions}/{segment.abandonedSessions}
                    </MiniValue>
                    <KpiSub>completed / in progress / abandoned</KpiSub>
                </div>
            </SegmentRow>
        </CardBody>
    </Card>
);

const SegmentsExplainer = styled.div`
    color: ${colorTextAdditional};
    font-size: 12px;
    background: ${colorBackground};
    border: 1px solid ${colorBorder};
    border-radius: 6px;
    padding: 10px 14px;
    margin-bottom: 14px;
    line-height: 1.5;
`;

// ------------------------------------------------------------------
// Page
// ------------------------------------------------------------------

const TelegramBotStatsPage = () => {
    const [period, setPeriod] = useState<TelegramStatsPeriod>('30d');
    const { data: statsResponse, isLoading, isError } = useTelegramBotStats(period);
    const stats: TelegramBotStats | undefined = statsResponse?.data ?? undefined;

    const funnelData = useMemo(() => {
        if (!stats) return [];
        // Only show steps that at least one session touched — avoids a wall
        // of empty rows for flows no user has entered yet.
        return stats.funnel
            .filter((f) => f.reached > 0 || f.stuckHere > 0)
            .map((f) => ({
                name: `${f.step}. ${f.name}`,
                flow: f.flow,
                Reached: f.reached,
                'Stuck here': f.stuckHere,
            }));
    }, [stats]);

    const personaData = useMemo(() => {
        if (!stats) return [];
        const t = stats.personaSplit.trader;
        const g = stats.personaSplit.general;
        return [
            { name: 'Trader — completed', value: t.completed, color: PERSONA_COLORS.traderCompleted },
            { name: 'Trader — in progress', value: t.inProgress, color: PERSONA_COLORS.traderInProgress },
            { name: 'Trader — abandoned', value: t.abandoned, color: PERSONA_COLORS.traderAbandoned },
            { name: 'General — completed', value: g.completed, color: PERSONA_COLORS.generalCompleted },
            { name: 'General — in progress', value: g.inProgress, color: PERSONA_COLORS.generalInProgress },
            { name: 'General — abandoned', value: g.abandoned, color: PERSONA_COLORS.generalAbandoned },
            { name: 'Dropped before gate', value: stats.personaSplit.preGate, color: PERSONA_COLORS.preGate },
        ].filter((d) => d.value > 0);
    }, [stats]);

    return (
        <Container>
            <Row>
                <Col md={12}>
                    <PageTitle className="page-title">Telegram Bot Stats</PageTitle>
                    <PageSubhead>Funnel and campaign performance for the Telegram onboarding bot</PageSubhead>
                </Col>
            </Row>

            <Row>
                <Col md={12}>
                    <FilterBar>
                        <PeriodLabel htmlFor="tg-stats-period">Period</PeriodLabel>
                        <PeriodSelect
                            id="tg-stats-period"
                            value={period}
                            onChange={(e) => setPeriod(e.target.value as TelegramStatsPeriod)}
                        >
                            {PERIOD_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </PeriodSelect>
                        {stats ? <GeneratedAt>Generated {new Date(stats.generatedAt).toLocaleString()}</GeneratedAt> : null}
                    </FilterBar>
                </Col>
            </Row>

            {isLoading ? (
                <Row>
                    <Col md={12}>
                        <Card>
                            <CardBody>
                                <div className="d-flex justify-content-center" style={{ padding: 40 }}>
                                    <LoadingSpinner />
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            ) : isError || !stats ? (
                <Row>
                    <Col md={12}>
                        <Card>
                            <CardBody>
                                <EmptyState>Failed to load stats. Check your connection or admin access.</EmptyState>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            ) : (
                <>
                    {/* Segments: Total / Campaign / Organic.
                        Campaign = sessions that arrived via a UTM-tagged /start
                        link from an ad. Organic = every other /start (direct,
                        shared link, user typed the bot handle). The percentages
                        per segment are only comparable within that segment; the
                        "Campaign" column is what marketing should read when
                        judging ad conversion. */}
                    <Row>
                        <Col md={12}>
                            <SegmentsExplainer>
                                <strong>Total</strong> = everyone who sent <code>/start</code>. <strong>Campaign</strong> = sessions with a UTM-tagged{' '}
                                <code>/start</code> parameter (i.e. arrived from an ad). <strong>Organic / direct</strong> = the remainder. Conversion
                                rates in the <em>Campaign</em> column are the numbers to judge ad-campaign performance against.
                            </SegmentsExplainer>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12} lg={4} className="mb-3">
                            <SegmentCard segment={stats.segments.total} hint="Every /start the bot received." />
                        </Col>
                        <Col md={12} lg={4} className="mb-3">
                            <SegmentCard
                                segment={stats.segments.campaign}
                                hint="Only sessions that arrived with UTM fields set."
                                primary
                            />
                        </Col>
                        <Col md={12} lg={4} className="mb-3">
                            <SegmentCard segment={stats.segments.organic} hint="Direct bot access with no UTM attribution." />
                        </Col>
                    </Row>

                    {/* Single "Dropped before gate" tile — it's a Total-only
                        metric; per-segment drop is visible in the status mix. */}
                    <Row>
                        <Col md={6} lg={3} className="mb-4">
                            <KpiTile
                                label="Dropped before gate (total)"
                                value={stats.personaSplit.preGate}
                                sub="Never answered the trader gate"
                            />
                        </Col>
                    </Row>

                    {/* Funnel + persona split */}
                    <Row>
                        <Col md={12} lg={8} className="mb-4">
                            <Card>
                                <CardBody>
                                    <CardTitleWrap>
                                        <CardTitle>Funnel — sessions reaching each step</CardTitle>
                                    </CardTitleWrap>
                                    {funnelData.length === 0 ? (
                                        <EmptyState>No sessions yet for this period.</EmptyState>
                                    ) : (
                                        <ResponsiveContainer width="100%" height={Math.max(320, funnelData.length * 28)}>
                                            <BarChart data={funnelData} layout="vertical" margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                                                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                                <XAxis type="number" allowDecimals={false} />
                                                <YAxis type="category" dataKey="name" width={240} tick={{ fontSize: 11 }} />
                                                <Tooltip />
                                                <Legend />
                                                <Bar dataKey="Reached" fill={colorAccent} />
                                                <Bar dataKey="Stuck here" fill={colorYellow} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>

                        <Col md={12} lg={4} className="mb-4">
                            <Card>
                                <CardBody>
                                    <CardTitleWrap>
                                        <CardTitle>Persona split</CardTitle>
                                    </CardTitleWrap>
                                    {personaData.length === 0 ? (
                                        <EmptyState>No sessions yet for this period.</EmptyState>
                                    ) : (
                                        <ResponsiveContainer width="100%" height={320}>
                                            <PieChart>
                                                <Pie
                                                    data={personaData}
                                                    dataKey="value"
                                                    nameKey="name"
                                                    cx="50%"
                                                    cy="50%"
                                                    outerRadius={100}
                                                    label={false}
                                                >
                                                    {personaData.map((entry, i) => (
                                                        <Cell key={i} fill={entry.color} />
                                                    ))}
                                                </Pie>
                                                <Tooltip />
                                                <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: 11 }} />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {/* Campaign table */}
                    <Row>
                        <Col md={12}>
                            <Card>
                                <CardBody>
                                    <CardTitleWrap>
                                        <CardTitle>Campaign performance</CardTitle>
                                    </CardTitleWrap>
                                    {stats.campaigns.length === 0 ? (
                                        <EmptyState>No campaign data yet.</EmptyState>
                                    ) : (
                                        <TableWrap>
                                            <StatsTable>
                                                <thead>
                                                    <tr>
                                                        <th>Campaign</th>
                                                        <th className="num">Sessions</th>
                                                        <th className="num">Completed</th>
                                                        <th className="num">Signed up</th>
                                                        <th className="num">Paid</th>
                                                        <th className="num">Completion</th>
                                                        <th className="num">Paid rate</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {stats.campaigns.map((c, i) => {
                                                        const compRate = c.sessions > 0 ? (c.completed / c.sessions) * 100 : 0;
                                                        const paidRate = c.sessions > 0 ? (c.paid / c.sessions) * 100 : 0;
                                                        return (
                                                            <tr key={i}>
                                                                <td>{formatCampaign(c)}</td>
                                                                <td className="num">{c.sessions}</td>
                                                                <td className="num">{c.completed}</td>
                                                                <td className="num">{c.signedUp}</td>
                                                                <td className="num">{c.paid}</td>
                                                                <td className="num">{formatPercent(compRate)}</td>
                                                                <td className="num">{formatPercent(paidRate)}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </StatsTable>
                                        </TableWrap>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {/* Ref source table — TG-Ads ref-routing performance.
                        Counts only sessions that arrived via a recognized
                        `ref_<value>` deep link (price / signup / onboarding /
                        21-campaign). UTM-tagged sessions live in the
                        Campaigns table above; this is a separate funnel. */}
                    <Row>
                        <Col md={12}>
                            <Card>
                                <CardBody>
                                    <CardTitleWrap>
                                        <CardTitle>Ref source performance</CardTitle>
                                    </CardTitleWrap>
                                    <SegmentsExplainer>
                                        Per-ref breakdown for TG-Ads <code>ref_&lt;value&gt;</code> deep links — separate from the UTM-tagged campaigns
                                        above. Only sessions that came in through the bot’s ref-routing appear here.
                                    </SegmentsExplainer>
                                    {(stats.refSources ?? []).length === 0 ? (
                                        <EmptyState>No ref-source data yet.</EmptyState>
                                    ) : (
                                        <TableWrap>
                                            <StatsTable>
                                                <thead>
                                                    <tr>
                                                        <th>Ref source</th>
                                                        <th className="num">Sessions</th>
                                                        <th className="num">Completed</th>
                                                        <th className="num">Signed up</th>
                                                        <th className="num">Paid</th>
                                                        <th className="num">Completion</th>
                                                        <th className="num">Paid rate</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {stats.refSources.map((r, i) => {
                                                        const compRate = r.sessions > 0 ? (r.completed / r.sessions) * 100 : 0;
                                                        const paidRate = r.sessions > 0 ? (r.paid / r.sessions) * 100 : 0;
                                                        return (
                                                            <tr key={i}>
                                                                <td>{r.refSource}</td>
                                                                <td className="num">{r.sessions}</td>
                                                                <td className="num">{r.completed}</td>
                                                                <td className="num">{r.signedUp}</td>
                                                                <td className="num">{r.paid}</td>
                                                                <td className="num">{formatPercent(compRate)}</td>
                                                                <td className="num">{formatPercent(paidRate)}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </StatsTable>
                                        </TableWrap>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </>
            )}
        </Container>
    );
};

export default TelegramBotStatsPage;
